/**
 * Which languages the app's own text is available in.
 *
 * The APP's language, not the document's. A judgment is published by the Court in its own
 * official versions and the corpus carries English and French of them; this list is about the
 * chrome around it — the tabs, the labels, the tooltips, the notices. The two are separate
 * settings and will diverge the moment a third language is added here, because there will be no
 * Greek judgment to switch to. See `LOCALE_TO_DOCUMENT_LANGUAGE` below.
 *
 * Three to begin with. The eventual target is the 24 official EU languages, and nothing in the
 * setup assumes otherwise — adding one is a file in `messages/` and an entry here. English and
 * French first because they are the two the corpus itself has, which makes the document-language
 * question testable rather than theoretical.
 */
export const LOCALES = ["en", "fr", "de"] as const

export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = "en"

/** What the app calls each language, in that language. A picker in English is no use to anyone. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  fr: "Français",
  de: "Deutsch",
}

/** The cookie the reader's choice is kept in. Read on the server, written by the switcher. */
export const LOCALE_COOKIE = "NEXT_LOCALE"

export function isLocale(value: string | undefined | null): value is Locale {
  return LOCALES.includes((value ?? "") as Locale)
}

/**
 * The document language to open a judgment in, for a reader of this app language.
 *
 * A separate axis with a much shorter list: the reader's `language` state chooses between the
 * Court's OWN versions, and the corpus has two. Mapping is a starting point rather than a
 * binding — the reader's control in the toolbar still wins once they touch it — and every
 * locale with no version in the corpus falls back to English, which is what there is.
 */
export const LOCALE_TO_DOCUMENT_LANGUAGE: Record<Locale, "english" | "french"> = {
  en: "english",
  fr: "french",
  // The corpus has no German version of anything. This is the case the mapping exists to make
  // explicit rather than to hide: the chrome is German and the judgment is in English, because
  // English is what there is.
  de: "english",
}
